import React, { useEffect, useState } from 'react';
import SEO from '../../components/SEO';
import { LightboxImage } from '../../components/LightboxImage';
import Navbar from '../../component/Navbar/Navbar';
import Footer from '../../component/Footer/Footer';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/client';
import './Profile.css';

const Profile = () => {
  const { refreshUser, user: authUser } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [governmentIdNumber, setGovernmentIdNumber] = useState('');
  const [nextOfKinName, setNextOfKinName] = useState('');
  const [nextOfKinPhone, setNextOfKinPhone] = useState('');
  const [nextOfKinRelationship, setNextOfKinRelationship] = useState('');
  const [fatherName, setFatherName] = useState('');
  const [motherName, setMotherName] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [govIdFile, setGovIdFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [serverAvatarUrl, setServerAvatarUrl] = useState(null);
  const [serverGovIdUrl, setServerGovIdUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get('/user/profile')
      .then((res) => {
        if (cancelled) return;
        const p = res.data !== undefined ? res.data : res;
        setEmail(p.email || '');
        setName(p.name || '');
        setPhoneNumber(p.phone_number || '');
        setGovernmentIdNumber(p.government_id_number || '');
        setNextOfKinName(p.next_of_kin_name || '');
        setNextOfKinPhone(p.next_of_kin_phone || '');
        setNextOfKinRelationship(p.next_of_kin_relationship || '');
        setFatherName(p.father_name || '');
        setMotherName(p.mother_name || '');
        setServerAvatarUrl(p.avatar_url || null);
        setServerGovIdUrl(p.government_id_url || null);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Could not load profile');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!avatarFile) {
      setAvatarPreview(null);
      return;
    }
    const url = URL.createObjectURL(avatarFile);
    setAvatarPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  const displayAvatar = avatarPreview || serverAvatarUrl;
  const displayEmail = email || authUser?.email || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      if (phoneNumber.trim()) {
        fd.append('phone_number', phoneNumber.trim());
      }
      if (avatarFile) {
        fd.append('avatar', avatarFile);
      }
      fd.append('government_id_number', governmentIdNumber.trim());
      fd.append('next_of_kin_name', nextOfKinName.trim());
      fd.append('next_of_kin_phone', nextOfKinPhone.trim());
      fd.append('next_of_kin_relationship', nextOfKinRelationship.trim());
      fd.append('father_name', fatherName.trim());
      fd.append('mother_name', motherName.trim());
      if (govIdFile) {
        fd.append('government_id', govIdFile);
      }
      const res = await api.post('/user/profile', fd);
      const p = res.data !== undefined ? res.data : res;
      setEmail(p.email || '');
      setName(p.name || '');
      setPhoneNumber(p.phone_number || '');
      setGovernmentIdNumber(p.government_id_number || '');
      setNextOfKinName(p.next_of_kin_name || '');
      setNextOfKinPhone(p.next_of_kin_phone || '');
      setNextOfKinRelationship(p.next_of_kin_relationship || '');
      setFatherName(p.father_name || '');
      setMotherName(p.mother_name || '');
      setServerAvatarUrl(p.avatar_url || null);
      setServerGovIdUrl(p.government_id_url || null);
      setAvatarFile(null);
      setGovIdFile(null);
      await refreshUser();
      setSuccess(true);
    } catch (err) {
      const bag = err?.data?.errors;
      if (bag && typeof bag === 'object') {
        const messages = Object.values(bag).flat().filter(Boolean);
        setError(messages.join(' ') || err.message);
      } else {
        setError(err.message || 'Could not save profile');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-page">
      <SEO
        title="Your profile"
        description="Update your Luxistt account name, phone number, and profile photo."
        path="/profile"
        noindex
      />
      <Navbar />
      <div className="profile-shell">
        <div className="profile-inner">
          <header className="profile-page-head">
            <p className="profile-page-kicker">Account</p>
            <h1 className="profile-page-title">Profile</h1>
            <p className="profile-page-lede">
              Used for bookings and identity checks where properties require it.
            </p>
          </header>

          {loading ? (
            <div className="profile-loading-card glass-panel">
              <div className="profile-loading-pulse" aria-hidden />
              <p className="profile-muted">Loading your profile…</p>
            </div>
          ) : (
            <form className="profile-layout" onSubmit={handleSubmit}>
              <aside className="profile-aside glass-panel">
                <div className="profile-avatar-ring">
                  {displayAvatar ? (
                    <LightboxImage
                      src={displayAvatar}
                      fullSrc={serverAvatarUrl || displayAvatar}
                      alt="Profile photo"
                      className="profile-avatar-lightbox"
                      imgClassName="profile-avatar-img"
                    />
                  ) : (
                    <span className="profile-avatar-initial" aria-hidden>
                      {(name || displayEmail || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <label className="profile-upload-btn">
                  <span>Change photo</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="profile-sr-only"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      setAvatarFile(f || null);
                      setSuccess(false);
                    }}
                  />
                </label>
                <p className="profile-aside-hint">JPEG, PNG, WebP · max 5 MB</p>
                {displayEmail ? (
                  <div className="profile-email-pill">
                    <span className="profile-email-pill-label">Signed in as</span>
                    <span className="profile-email-pill-value">{displayEmail}</span>
                  </div>
                ) : null}
              </aside>

              <div className="profile-main">
                <section className="profile-section glass-panel">
                  <div className="profile-section-head profile-section-head--compact">
                    <h2 className="profile-section-title">Contact</h2>
                  </div>
                  <div className="profile-field-grid profile-field-grid--2">
                    <label className="profile-field">
                      <span className="profile-field-label">Full name</span>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoComplete="name"
                        maxLength={255}
                      />
                    </label>
                    <label className="profile-field">
                      <span className="profile-field-label">Phone (optional)</span>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        autoComplete="tel"
                        placeholder="+2348012345678"
                      />
                    </label>
                  </div>
                </section>

                <section className="profile-section glass-panel profile-section--govid">
                  <div className="profile-section-head profile-section-head--compact">
                    <h2 className="profile-section-title">Government ID</h2>
                  </div>
                  <div className="profile-field-grid profile-field-grid--govid">
                    <label className="profile-field">
                      <span className="profile-field-label">ID / passport number (optional)</span>
                      <input
                        type="text"
                        value={governmentIdNumber}
                        onChange={(e) => setGovernmentIdNumber(e.target.value)}
                        autoComplete="off"
                        maxLength={128}
                        placeholder="Number as on document"
                      />
                    </label>
                    <label className="profile-field profile-field--file">
                      <span className="profile-field-label">ID photo (optional)</span>
                      <span className="profile-file-row">
                        <span className="profile-file-name">{govIdFile ? govIdFile.name : 'No file chosen'}</span>
                        <span className="profile-file-browse">Browse</span>
                      </span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="profile-sr-only"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          setGovIdFile(f || null);
                          setSuccess(false);
                        }}
                      />
                    </label>
                  </div>
                  {serverGovIdUrl ? (
                    <div className="profile-govid-onfile">
                      <p className="profile-govid-onfile-hint">Current ID on file — click the image to view full size.</p>
                      <LightboxImage
                        src={serverGovIdUrl}
                        fullSrc={serverGovIdUrl}
                        alt="Government ID on file"
                        className="profile-govid-lightbox"
                        imgClassName="profile-govid-thumb"
                      />
                    </div>
                  ) : null}
                </section>

                <section className="profile-section glass-panel">
                  <div className="profile-section-head profile-section-head--compact">
                    <h2 className="profile-section-title">Emergency contact</h2>
                  </div>
                  <div className="profile-field-grid profile-field-grid--2">
                    <label className="profile-field">
                      <span className="profile-field-label">Name (optional)</span>
                      <input
                        type="text"
                        value={nextOfKinName}
                        onChange={(e) => setNextOfKinName(e.target.value)}
                        autoComplete="off"
                        maxLength={255}
                      />
                    </label>
                    <label className="profile-field">
                      <span className="profile-field-label">Phone (optional)</span>
                      <input
                        type="tel"
                        value={nextOfKinPhone}
                        onChange={(e) => setNextOfKinPhone(e.target.value)}
                        autoComplete="off"
                        placeholder="+2348012345678"
                        maxLength={64}
                      />
                    </label>
                    <label className="profile-field profile-field--span2">
                      <span className="profile-field-label">Relationship (optional)</span>
                      <input
                        type="text"
                        value={nextOfKinRelationship}
                        onChange={(e) => setNextOfKinRelationship(e.target.value)}
                        autoComplete="off"
                        placeholder="e.g. Spouse"
                        maxLength={120}
                      />
                    </label>
                  </div>
                </section>

                <section className="profile-section glass-panel">
                  <div className="profile-section-head profile-section-head--compact">
                    <h2 className="profile-section-title">Parents</h2>
                  </div>
                  <div className="profile-field-grid profile-field-grid--2">
                    <label className="profile-field">
                      <span className="profile-field-label">Father&apos;s name (optional)</span>
                      <input
                        type="text"
                        value={fatherName}
                        onChange={(e) => setFatherName(e.target.value)}
                        autoComplete="off"
                        maxLength={255}
                      />
                    </label>
                    <label className="profile-field">
                      <span className="profile-field-label">Mother&apos;s name (optional)</span>
                      <input
                        type="text"
                        value={motherName}
                        onChange={(e) => setMotherName(e.target.value)}
                        autoComplete="off"
                        maxLength={255}
                      />
                    </label>
                  </div>
                </section>

                <div className="profile-actions glass-panel">
                  {error ? (
                    <div className="profile-alert profile-alert--error" role="alert">
                      {error}
                    </div>
                  ) : null}
                  {success ? (
                    <div className="profile-alert profile-alert--success" role="status">
                      Profile saved successfully.
                    </div>
                  ) : null}
                  <button type="submit" className="btn-primary profile-submit" disabled={saving}>
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Profile;
